/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface ProgramProps {
  id: string;
  name: string;
  abbreviation: string;
  code: string;
  adminId: string | null;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class ProgramRecord extends Record<ProgramProps>({
  id: null as any,
  name: null as any,
  abbreviation: null as any,
  code: null as any,
  adminId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    abbreviation: string;
    code: string;
    adminId: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): ProgramRecord {
    if (json == null) {
      throw new Error('ProgramRecord.fromJS: parameter cannot be null');
    }
    return new ProgramRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'ProgramJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'ProgramJson.name'),
      abbreviation: getFromOrThrow(json, (j) => j.abbreviation, 'ProgramJson.abbreviation'),
      code: getFromOrThrow(json, (j) => j.code, 'ProgramJson.code'),
      adminId: json.adminId,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'ProgramJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    abbreviation: string;
    code: string;
    adminId: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [ProgramRecord, Error[]] {
    if (json == null) {
      throw new Error('ProgramRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new ProgramRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'ProgramJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'ProgramJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      abbreviation: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.abbreviation, 'ProgramJson.abbreviation'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      code: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.code, 'ProgramJson.code'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      adminId: tryOrDefault(
        () => json.adminId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'ProgramJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: ProgramProps) {
    super(props);
    if (props == null) {
      throw new Error('ProgramRecord constructor parameter cannot be null');
    }
  }
}

export abstract class ProgramViewModelBase implements ProgramProps {
  protected readonly _props: ProgramProps;
  private _json?: ProgramProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get abbreviation(): string {
    return this._props.abbreviation;
  }
  get code(): string {
    return this._props.code;
  }
  get adminId(): string | null {
    return this._props.adminId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: ProgramProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        abbreviation: this._props.abbreviation,
        code: this._props.code,
        adminId: this._props.adminId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllProgramEntities = (state: AppState) => state.entityStores.program.entities;

export const selectProgramById = (id: string) => (state: AppState) =>
  state.entityStores.program.entities.get(id);

export const selectProgramMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.program.meta.get(id);

export const selectProgramAndMetadataById = (id: string) =>
  createSelector(selectProgramById(id), selectProgramMetadataById(id), (program, meta) => ({
    program,
    meta,
  }));

export function selectProgramEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllProgramEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, ProgramRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectProgramEntitiesByName(name: string) {
  return createSelector(selectAllProgramEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectProgramEntitiesByAbbreviation(abbreviation: string) {
  return createSelector(selectAllProgramEntities, (entities) =>
    entities.filter((e) => e.abbreviation === abbreviation).valueSeq(),
  );
}

export function selectProgramEntitiesByCode(code: string) {
  return createSelector(selectAllProgramEntities, (entities) =>
    entities.filter((e) => e.code === code).valueSeq(),
  );
}

export function selectProgramEntitiesByAdminId(adminId: string) {
  return createSelector(selectAllProgramEntities, (entities) =>
    entities.filter((e) => e.adminId === adminId).valueSeq(),
  );
}

export function selectProgramEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllProgramEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
